import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LockOpen, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';
import { adminEmergency } from '@/api/admin';
import { useSystemStore } from '@/stores/system';
import { ApiError } from '@/lib/http';
import { fmtDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';

interface Props {
  variant?: 'primary' | 'compact';
  className?: string;
}

/**
 * 解除交易锁定按钮 — 摆在「一键强制平仓」右侧(仪表板 / TopBar)。
 *
 * 强平后系统把 `trade_lock_until` 锁到次日开盘;本按钮让管理员在解锁时刻前提前恢复交易。
 * 仅在 `tradeLocked` 且非强平进行中时可点;需填原因(与强平 / 解除停机一致)。
 * 后端若处于紧急停机会返回 409,引导改走「解除紧急停机」。
 */
export function UnlockTradeButton({ variant = 'primary', className }: Props) {
  const tradeLocked = useSystemStore((s) => s.tradeLocked);
  const unlockAt = useSystemStore((s) => s.unlockAt);
  const liquidating = useSystemStore((s) => s.liquidation.active);
  const setTradeLock = useSystemStore((s) => s.setTradeLock);
  const qc = useQueryClient();

  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setReason('');
    }
  }, [open]);

  const submit = useMutation({
    mutationFn: () => adminEmergency.clearTradeLock({ reason: reason.trim() }),
    onSuccess: () => {
      // 乐观更新让徽章/倒计时即时消失,WS `trade_lock_changed(false)` 再兜底一致。
      setTradeLock(false, null);
      void qc.invalidateQueries({ queryKey: ['admin'] });
      toast.success('已解除交易锁定', { description: '当前在交易窗口内将立即恢复跟单与下单' });
      setOpen(false);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409 && err.details?.['emergencyStop']) {
        toast.warning('当前处于紧急停机', {
          description: '请改用顶栏「解除紧急停机」,复核未处置敞口后会一并解锁交易',
        });
        return;
      }
      toast.error('解除锁定失败', {
        description: err instanceof ApiError ? err.message : '请稍后重试',
      });
    },
  });

  const disabled = !tradeLocked || liquidating;
  const reasonOk = reason.trim().length >= 4;
  const canSubmit = reasonOk && !submit.isPending;

  const trigger =
    variant === 'compact' ? (
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className={className}
      >
        <LockOpen className="h-4 w-4" />
        <span className="hidden sm:inline">解除锁定</span>
      </Button>
    ) : (
      <Button
        variant="outline"
        size="xl"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className={cn('text-base font-bold', className)}
      >
        <LockOpen className="h-5 w-5" />
        解除锁定
      </Button>
    );

  return (
    <>
      {trigger}

      <Dialog open={open} onOpenChange={(v) => !v && !submit.isPending && setOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-brand" />
              解除交易锁定
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2">
                <span className="block">
                  解除后将清除强平/结算写入的交易锁,把时段恢复为自然态:
                  当前在交易窗口内会<strong>立即恢复</strong>跟单与下单,窗口外仍按自然时段到下一开盘。
                </span>
                {unlockAt && (
                  <span className="block text-xs text-ink-2 num">
                    当前锁定至:{fmtDateTime(unlockAt)}
                  </span>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="ul-reason">操作原因(至少 4 字)</Label>
              <Input
                id="ul-reason"
                placeholder="例如:误触强平,提前恢复当日交易"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={submit.isPending}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submit.isPending}>
              取消
            </Button>
            <Button onClick={() => submit.mutate()} disabled={!canSubmit}>
              {submit.isPending ? '解除中…' : '确认解除'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
